import * as Sentry from "@sentry/react-native";
import {
    EarningType,
    Gender,
    Maybe,
    Mission,
    MissionInfo,
    MissionInput,
    PayrollSourceType,
    ProfileType,
    Repetition,
    SalaryDeductionType,
    SalaryType,
    ScheduleTimeType,
    Unit,
    WorkingTimeClassicType,
} from "../__generated__/graphql";
import {
    CoreContractFieldsFragment,
    CoreMissionFieldsFragment,
} from "../__generated__/operations/graphql";
import { addMonths, differenceInYears, format } from "date-fns";
import { Buildable } from "ts-essentials";
import { Linking, Platform } from "react-native";
import Constants from "expo-constants";
import { SearchItemsFunction } from "../components/select/SelectChooser";
import { SelectItemType } from "../components/select/SelectChooser";
import { IconName } from "../components/Icon";
import { NotificationContent } from "expo-notifications";
import { pick } from "lodash";

export const DEFAULT_HOURS_PER_WEEK = 42;
export const MAX_HOURS_PER_WEEK = 51;
export const WORKING_DAYS_PER_WEEK = 5;
export const MINIMUM_SLOT_DURATION_HOURS = 1;
export const MIDNIGHT_MINUTES = 24 * 60;
export const SYSTEM_NAME = "Lift";

export function convertUrlToCustomScheme(url: string) {
    const urlObject = new URL(url);
    return `${
        (Constants.expoConfig?.extra?.scheme as string) || "lift"
    }://${urlObject.pathname.substring(1)}`;
}

export function isFunction(f: unknown): boolean {
    return typeof f === "function";
}

export function isPromise<V>(p: unknown): p is Promise<V> {
    return (
        p !== null &&
        typeof p === "object" &&
        typeof (p as Record<string, unknown>).then === "function" &&
        typeof (p as Record<string, unknown>).catch === "function"
    );
}

export const range = (x: number, y?: number): number[] => {
    if (y === undefined) {
        return [...Array(x).keys()];
    }
    if (x >= y) {
        throw new Error(`x ${x} must be smaller than y ${y}`);
    }
    return [...Array(y - x + 1).keys()].map((i) => i + x);
};

export const hasLength = <T = unknown>(arr: T[] | null | undefined): arr is Array<T> => {
    return arr !== null && arr !== undefined && arr.length !== 0;
};

export const minutesAsString = (
    minutes: number,
    options: {
        separator?: string;
        showTrailingZero?: boolean;
        zeroPlaceholder?: string;
        showSeparatorSpace?: boolean;
    } = {},
) => {
    if (minutes === 0 && options.zeroPlaceholder !== undefined) {
        return options.zeroPlaceholder;
    }
    const { separator = "h", showTrailingZero = false, showSeparatorSpace = true } = options;
    const hours = Math.floor(Math.abs(minutes) / 60);
    const minutesLeft = Math.abs(minutes % 60);

    const separatorSpace = showSeparatorSpace ? " " : "";

    if (minutesLeft === 0 && !showTrailingZero) {
        return `${hours.toString()}${separatorSpace}${separator}`;
    }
    return `${hours.toString()}${separatorSpace}${separator}${separatorSpace}${minutesLeft.toString().padStart(2, "0")}`;
};

export const daysAsString = (
    days: number,
    options: { suffix?: string; zeroPlaceholder?: string } = {},
) => {
    if (days === 0 && options.zeroPlaceholder !== undefined) {
        return options.zeroPlaceholder;
    }
    const { suffix = "j" } = options;

    const fullDays = Math.floor(days);
    const decimalDays = parseFloat((days - fullDays).toFixed(1));
    let decimalString = "";
    if (decimalDays === 0.5) {
        // 1/2 string in ascii
        decimalString = "½";
    } else if (decimalDays !== 0) {
        decimalString = `.${decimalDays * 10}`;
    }
    return `${fullDays}${decimalString} ${suffix}`;
};

export const capitalizeFirstLetter = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

export const formatActivityTypeTitle = (activityName: string, gender?: Gender | null) => {
    const elements = activityName.split("|").map((s) => s.trim());
    if (gender === Gender.Female && elements.length > 1) {
        return elements[1];
    } else if (gender === Gender.Male) {
        return elements[0];
    }
    return elements.join(" / ");
};

export const formatMissionWorkerName = (mission: CoreMissionFieldsFragment | MissionInfo) => {
    return mission.workerInfo?.name;
};

export const formatMissionClientName = (mission: CoreMissionFieldsFragment | MissionInfo) => {
    return mission.clientInfo?.name;
};

export const formatMissionClientProfileName = (
    mission: CoreMissionFieldsFragment | MissionInfo,
) => {
    return mission.clientInfo?.profileName;
};

export const createMissionInputFromMission = (mission: Partial<MissionWithIjm>): MissionInput => {
    return {
        name: mission.name,
        lang: mission.workerInfo?.lang,
        activityTypeId: mission.activityType?.id,
        clientProfileId: mission.clientInfo?.id,
        workerProfileId: mission.workerInfo?.id,
        educationLevel: mission.educationLevel,
        documents: mission.documents,
        payrollSource: mission.payrollSource,
        workerGroupId: mission.workerGroup?.id,
        contract: {
            startDate: mission.currentContract?.startDate,
            endDate: mission.currentContract?.endDate,
            jobTitle: getMissionJobTitleDisplay(mission as CoreMissionFieldsFragment),
            jobDescription: mission.currentContract?.jobDescription,
            cctEditionId: mission.currentContract?.cctEditionId,
            cctDetails: mission.currentContract?.cctDetails,
            workingAddress: mission.currentContract?.workingAddress,
            earningType: mission.currentContract?.earningType,
            baseSalary: mission.currentContract?.baseSalary,
            grossSalary: mission.currentContract?.grossSalary,
            has13thSalary: mission.currentContract?.has13thSalary,
            pay13thSalaryWithSalary: getIsPay13thSalaryWithSalary(mission.currentContract),
            vacationDays: mission.currentContract?.vacationDays,
            disableAvsSmallSalaryExemption: mission.currentContract?.disableAvsSmallSalaryExemption,
            forceLpp: mission.currentContract?.forceLpp,
            workingTimeClassicSpec: mission.currentContract?.workingTimeClassicSpec
                ? mission.currentContract?.workingTimeClassicSpec
                : undefined,
            fees: mission.currentContract?.fees,
            deductions: mission.currentContract?.deductions,
            hoursPerWeekFullTime: mission.currentContract?.hoursPerWeekFullTime,
            lppPlanId: mission.currentContract?.lppPlan?.id,
            payVacationIndemnityWithSalary:
                !!mission.currentContract?.payVacationIndemnityWithSalary,
        },
        ijmSelection: pick(
            mission.ijmSelection,
            "ijmGroupId",
            "ijmPayFirstDays",
            "ijmPayRemainingPercents",
        ),
        legalEmployerId: mission.legalEmployer?.id,
    };
};

export const getAge = (aDate: Date) => {
    return differenceInYears(new Date(), aDate);
};

export const getWorkingTimeClassicPercentage = (
    spec: WorkingTimeClassicType,
    hoursPerWeekFull = DEFAULT_HOURS_PER_WEEK,
) => {
    const hoursPerDayFull = hoursPerWeekFull / 5;
    const hoursPerMonthFull = hoursPerWeekFull * 4.33;
    if (spec && spec.unitCount !== undefined && spec.unitCount !== null) {
        if (spec.unit == Unit.Days && spec.repetition == Repetition.Week) {
            const dailyHours = spec.dailyHours as number;
            return Math.round((spec.unitCount * dailyHours * 100) / hoursPerWeekFull);
        }
        if (spec.unit == Unit.Days && spec.repetition == Repetition.Month) {
            const dailyHours = spec.dailyHours as number;
            return Math.round((spec.unitCount * dailyHours * 100) / hoursPerMonthFull);
        }
        if (spec.unit == Unit.Hours && spec.repetition == Repetition.Day) {
            return Math.round((spec.unitCount * 100) / hoursPerDayFull);
        }
        if (spec.unit == Unit.Hours && spec.repetition == Repetition.Week) {
            return Math.round((spec.unitCount * 100) / hoursPerWeekFull);
        }
        if (spec.unit == Unit.Hours && spec.repetition == Repetition.Month) {
            return Math.round((spec.unitCount * 100) / hoursPerMonthFull);
        }
    }
    return undefined;
};

export const getWorkingTimeClassicWeeklyDuration = (
    spec: WorkingTimeClassicType,
    hoursPerWeekFull = DEFAULT_HOURS_PER_WEEK,
) => {
    const hoursPerDayFull = hoursPerWeekFull / 5;
    if (spec && spec.unitCount !== undefined && spec.unitCount !== null) {
        if (spec.unit == Unit.Days && spec.repetition == Repetition.Week) {
            return Math.round(spec.unitCount * hoursPerDayFull * 60);
        }
        if (spec.unit == Unit.Days && spec.repetition == Repetition.Month) {
            return Math.round((spec.unitCount * hoursPerDayFull * 60) / 4.33);
        }
        if (spec.unit == Unit.Hours && spec.repetition == Repetition.Day) {
            return Math.round(spec.unitCount * 60 * 5);
        }
        if (spec.unit == Unit.Hours && spec.repetition == Repetition.Week) {
            return Math.round(spec.unitCount * 60);
        }
        if (spec.unit == Unit.Hours && spec.repetition == Repetition.Month) {
            return Math.round((spec.unitCount * 60) / 4.33);
        }
    }
    return undefined;
};

/** An on-demand ("sur appel") contract guarantees no volume of work: its spec carries no unitCount. */
export const isOnDemandWorkingTime = (spec?: Maybe<{ repetition?: Maybe<Repetition> }>): boolean =>
    spec?.repetition === Repetition.OnDemand;

/** No vacation is taken on a one-shot (punctual) nor on an on-demand contract. */
export const isVacationFreeWorkingTime = (
    spec?: Maybe<{ repetition?: Maybe<Repetition> }>,
): boolean => spec?.repetition === Repetition.None || isOnDemandWorkingTime(spec);

export const getMissionPercentage = (mission: Mission) => {
    if (mission.currentContract) {
        return getContractPercentage(mission.currentContract);
    }
    return undefined;
};

export const getContractPercentage = (contract: CoreContractFieldsFragment) => {
    const hoursPerWeekFull = contract.hoursPerWeekFullTime || DEFAULT_HOURS_PER_WEEK;

    const spec = contract.workingTimeClassicSpec;
    if (spec) {
        return getWorkingTimeClassicPercentage(spec, hoursPerWeekFull);
    }
    // default is 100% if we cannot compute
    return 100;
};

/**
 * Whether vacations are managed for a contract. They are not managed for short missions:
 * forfait/package, non-recurring, or a fixed-term contract (CDD) shorter than 3 months, nor for
 * an on-demand contract.
 * When vacations are not managed, the indemnity is always paid directly (never provisioned).
 */
export const areVacationsManaged = (
    contract?: {
        earningType?: Maybe<EarningType>;
        startDate?: Maybe<string>;
        endDate?: Maybe<string>;
        workingTimeClassicSpec?: Maybe<{ repetition?: Maybe<Repetition> }>;
    } | null,
): boolean => {
    if (!contract) {
        return false;
    }
    const isHourlyOrDaily =
        contract.earningType === EarningType.Hourly || contract.earningType === EarningType.Daily;
    const isRecurring = !isVacationFreeWorkingTime(contract.workingTimeClassicSpec);
    // A fixed-term contract (CDD, i.e. with an end date) shorter than 3 months is a short mission.
    const isShortFixedTerm =
        !!contract.startDate &&
        !!contract.endDate &&
        new Date(contract.endDate) < addMonths(new Date(contract.startDate), 3);
    return isHourlyOrDaily && isRecurring && !isShortFixedTerm;
};

/**
 * Whether the vacation indemnity is paid out with each salary (as opposed to being held as a
 * provision / "cagnotte"). When it is NOT paid with salary, the salary breakdown splits the net
 * salary into a paid part and a held provision ("Provisions vacances").
 *
 * Rules:
 * - Vacations not managed (short mission) → always paid with salary.
 * - Cagnotting mandatory (legal employer uses LSE, or activity rate is 100%) → never paid with
 *   salary (always held as a provision).
 * - Otherwise → follows the contract's payVacationIndemnityWithSalary choice.
 */
export const getIsPayVacationIndemnityWithSalary = (
    contract:
        | {
              earningType?: Maybe<EarningType>;
              payVacationIndemnityWithSalary?: Maybe<boolean>;
              startDate?: Maybe<string>;
              endDate?: Maybe<string>;
              workingTimeClassicSpec?: Maybe<{ repetition?: Maybe<Repetition> }>;
          }
        | null
        | undefined,
    useLseCct?: Maybe<boolean>,
): boolean => {
    if (!contract || !areVacationsManaged(contract)) {
        return true;
    }
    const isCagnotteMandatory =
        !!useLseCct || getContractPercentage(contract as CoreContractFieldsFragment) === 100;
    return !isCagnotteMandatory && !!contract.payVacationIndemnityWithSalary;
};

/**
 * The part of the net salary actually paid out each period when an indemnity is held back as a
 * provision (vacation "cagnotte", or a 13x 13th salary), in cents.
 *
 * `deferredIndemnity` is the share of that indemnity the backend already left out of the net (the
 * contributions on it are charged when it is paid out). It is 0 under an LSE legal employer, where
 * the net still carries the whole indemnity; so only the remainder is taken out here.
 */
export const getPaidSalary = ({
    netSalary,
    heldBackIndemnity,
    deferredIndemnity,
}: {
    netSalary: number;
    heldBackIndemnity: number;
    deferredIndemnity?: Maybe<number>;
}): number => netSalary - (heldBackIndemnity - (deferredIndemnity ?? 0));

type ThirteenthSalaryContract =
    | {
          earningType?: Maybe<EarningType>;
          has13thSalary?: Maybe<boolean>;
          pay13thSalaryWithSalary?: Maybe<boolean>;
      }
    | null
    | undefined;

/**
 * Whether the 13th salary is paid with every salary (12x), as opposed to held back and paid out
 * separately (13x): in December, on the contract's last period, or on request. Only a monthly
 * contract with a 13th can be 13x; anything else resolves to 12x, the backend's normalisation.
 */
export const getIsPay13thSalaryWithSalary = (contract: ThirteenthSalaryContract): boolean =>
    !(
        contract?.earningType === EarningType.Monthly &&
        contract.has13thSalary === true &&
        contract.pay13thSalaryWithSalary === false
    );

/**
 * The salary type to request for a contract's 12x/13x regime. On a 13x contract the monthly amount
 * paid is the base salary (the 13th share of the gross is held back), so the gross option becomes
 * the base; back on 12x it is the gross again. Net and invoice are left as they are.
 */
export const getSalaryTypeForRegime = (salaryType: SalaryType, is13x: boolean): SalaryType => {
    if (is13x && salaryType === SalaryType.Gross) {
        return SalaryType.Base;
    }
    if (!is13x && salaryType === SalaryType.Base) {
        return SalaryType.Gross;
    }
    return salaryType;
};

export const getContractWeeklyDurationInMinutes = (contract: CoreContractFieldsFragment) => {
    const hoursPerWeekFull = contract.hoursPerWeekFullTime || DEFAULT_HOURS_PER_WEEK;
    const spec = contract.workingTimeClassicSpec;
    if (spec) {
        return getWorkingTimeClassicWeeklyDuration(spec, hoursPerWeekFull);
    }
    // default is undefined
    return undefined;
};

const WORKING_TIME_BASE_I18N_MAP = {
    [Unit.Hours]: "nb_hours",
    [Unit.Days]: "nb_days",
};

const WORKING_TIME_REPEAT_I18N_MAP: Record<
    Exclude<Repetition, Repetition.OnDemand>,
    [string, number]
> = {
    [Repetition.Day]: ["repeat_day", 1],
    [Repetition.Week]: ["repeat_week", 1],
    [Repetition.Week2]: ["repeat_week", 2],
    [Repetition.Week3]: ["repeat_week", 3],
    [Repetition.Week4]: ["repeat_week", 4],
    [Repetition.Month]: ["repeat_month", 1],
    [Repetition.None]: ["repeat_none", 0],
};

type I18nInterface = {
    t: (key: string, params?: Record<string, unknown>) => string;
};
export const formatWorkingTimeClassicSpec = (
    workingTimeSpec: Buildable<WorkingTimeClassicType>,
    i18n: I18nInterface,
) => {
    const { repetition, unit, unitCount, dailyHours } = workingTimeSpec;

    if (repetition === Repetition.OnDemand) {
        return i18n.t("utils.working_time.on_demand.summary");
    }
    if (!unit || !repetition || (unit === Unit.Days && repetition === Repetition.Day)) {
        return i18n.t("utils.working_time.invalid");
    }

    const workingTimeTranslationKey = WORKING_TIME_BASE_I18N_MAP[unit];
    const [workingTimeTranslationKeyExt, repCount] = WORKING_TIME_REPEAT_I18N_MAP[repetition];

    const base = i18n
        .t(`utils.working_time.classic.${workingTimeTranslationKey}`, {
            count: unitCount || 0,
        })
        .replace(".", ",");
    const ext = i18n.t(`utils.working_time.classic.${workingTimeTranslationKeyExt}`, {
        count: repCount,
    });
    const elements = [];
    elements.push(`${base} ${ext}`);
    if (dailyHours) {
        elements.push(i18n.t("utils.working_time.daily_hours", { count: dailyHours }));
    }
    return elements.join("\n");
};

export const isLocale24h = !new Intl.DateTimeFormat([], { hour: "numeric" })
    .format(new Date(0))
    .match(/AM/);

export enum DayOfWeek {
    Monday = "mon",
    Tuesday = "tue",
    Wednesday = "wed",
    Thursday = "thu",
    Friday = "fri",
    Saturday = "sat",
    Sunday = "sun",
}

export const convertIntegerDayOfWeek = (
    dow: number,
): "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun" => {
    switch (dow) {
        case 0:
            return "sun";
        case 1:
            return "mon";
        case 2:
            return "tue";
        case 3:
            return "wed";
        case 4:
            return "thu";
        case 5:
            return "fri";
        case 6:
            return "sat";
        default:
            throw new Error("Invalid day of week");
    }
};

export const openPhoneNumber = async (phoneNumber: string) => {
    const phone = encodeURIComponent(phoneNumber);
    await Linking.openURL(`tel:${phone}`);
};

type TagType = {
    icon: IconName;
    value: ScheduleTimeType;
};

export const scheduleTimeTypeTags = [
    { icon: "work", value: ScheduleTimeType.Work },
    { icon: "holiday", value: ScheduleTimeType.PublicHoliday },
    { icon: "vacation", value: ScheduleTimeType.Vacation },
    { icon: "military", value: ScheduleTimeType.Military },
    { icon: "sickness", value: ScheduleTimeType.Sickness },
    { icon: "accident", value: ScheduleTimeType.Accident },
    { icon: "maternity", value: ScheduleTimeType.Maternity },
    { icon: "training", value: ScheduleTimeType.Trainee },
    { icon: "sickness_close_relative", value: ScheduleTimeType.SicknessCloseRelative },
    { icon: "death_close_relative", value: ScheduleTimeType.DeathCloseRelative },
    { icon: "marriage", value: ScheduleTimeType.Marriage },
    { icon: "moving", value: ScheduleTimeType.Moving },
    { icon: "head", value: ScheduleTimeType.Other },
] as TagType[];

export function formatDateForGraphql(date: Date) {
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;
}

export const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export const processSelectItems = <V>(
    searchItems: SearchItemsFunction<V> | SelectItemType<V>[] | Promise<SelectItemType<V>[]> | null,
    searchQuery = "",
    setItems?: (items: SelectItemType<V>[]) => void,
) => {
    let isStaticItems: boolean;
    let items: SelectItemType<V>[] = [];

    if (typeof searchItems === "function") {
        isStaticItems = false;
        const asyncFilter = async () => {
            const matchingItems = await searchItems(searchQuery.trim());
            setItems?.(matchingItems || []);
        };
        void asyncFilter();
    } else if (isPromise<SelectItemType<V>[]>(searchItems)) {
        isStaticItems = true;

        const asyncFilter = async () => {
            const result: SelectItemType<V>[] = await searchItems;
            setItems?.(result || []);
        };
        void asyncFilter();
    } else if (Array.isArray(searchItems)) {
        isStaticItems = true;
        items = searchItems;
        setItems?.(searchItems);
    } else {
        isStaticItems = true;
        items = [];
        setItems?.([]);
    }

    return { isStaticItems, items };
};

export function getWeekNumber(date: Date): [number, number] {
    // Copy date so don't modify original
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    // Set to nearest Thursday: current date + 4 - current day number
    // Make Sunday's day number 7
    d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
    // Get first day of year
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    // Calculate full weeks to nearest Thursday
    const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
    // Return array of year and week number
    return [d.getUTCFullYear(), weekNo];
}

type DirectionType =
    | "bottom"
    | "top"
    | "left"
    | "right"
    | "center"
    | "topLeft"
    | "topRight"
    | "bottomLeft"
    | "bottomRight";

const adjustShadowOffsetOnDirection = (level: number, direction: DirectionType) => {
    const distance = Math.floor(level * 0.5);
    switch (direction) {
        case "bottom":
            return { width: 0, height: distance };
        case "top":
            return { width: 0, height: -distance };
        case "left":
            return { width: -distance, height: 0 };
        case "right":
            return { width: distance, height: 0 };
        case "center":
            return { width: 0, height: 0 };
        case "topLeft":
            return { width: -distance, height: -distance };
        case "topRight":
            return { width: distance, height: -distance };
        case "bottomLeft":
            return { width: -distance, height: distance };
        case "bottomRight":
            return { width: -distance, height: distance };
        default:
            return { width: 0, height: distance };
    }
};

const interpolate = (i: number, a: number, b: number, a2: number, b2: number) => {
    return ((i - a) * (b2 - a2)) / (b - a) + a2;
};

/**
 * @param {number} level Shadow level you want to set (default to 4)
 * @param {string} shadowColor Shadow color you want to set (default to Black)
 * @param {string} direction In which direction the shadow should be displayed (default to bottom)
 */
export const getShadow = (params?: {
    level?: number;
    shadowColor?: string;
    direction?: DirectionType;
}) => {
    const level = (params?.level ?? 4) * (Platform.OS === "android" ? 1.5 : 1);
    const direction = params?.direction || "bottom";
    const shadowOffset = adjustShadowOffsetOnDirection(level, direction);
    const shadowOpacity = Number(interpolate(level, 1, 24, 0.2, 0.6).toFixed(2));
    const shadowRadius = Number(interpolate(level, 1, 38, 1, 16).toFixed(2));
    const shadowColor = hexToRgba(params?.shadowColor || "#000000", shadowOpacity);

    return {
        elevation: level,
        shadowColor: Platform.OS === "android" ? params?.shadowColor : shadowColor,
        shadowOffset: shadowOffset,
        shadowRadius: shadowRadius,
        shadowOpacity: 1,
    };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const deepValuesMap = (obj: any, ignoreKey?: string) => {
    let out: string[] = [];

    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    Object.keys(obj).forEach(function (k) {
        let val;

        if (k === ignoreKey) {
            return out;
        }

        if (obj[k] !== null && typeof obj[k] === "object") {
            val = deepValuesMap(obj[k], ignoreKey);
        } else {
            val = obj[k];
        }

        out = [...out, ...(Array.isArray(val) ? val : [val])];
    });

    return out;
};

// check here: https://fedlex.data.admin.ch/filestore/fedlex.data.admin.ch/eli/cc/2010/96/20140301/fr/pdf-a/fedlex-data-admin-ch-eli-cc-2010-96-20140301-fr-pdf-a.pdf
export const ALLOWED_NAME_CHARACTERS = [32, 39, 45, 46]
    .concat(range(65, 90))
    .concat(range(97, 122))
    .concat([138, 140, 142, 154, 156, 158, 159])
    .concat(range(192, 214))
    .concat(range(216, 246))
    .concat(range(248, 255));

export const isAllowedNameCharacter = (char: string) => {
    return ALLOWED_NAME_CHARACTERS.includes(char.charCodeAt(0));
};

export const isAllowedPersonName = (name: string) => {
    return name.split("").every(isAllowedNameCharacter);
};

/**
 * Check if a person name is valid, return the i18n code if any error, false otherwise
 * @param name person firstname or lastname
 */
export const checkPersonNameValidity = (name?: string): string | null | undefined => {
    if (!name) {
        return "user.errors.name_missing";
    }
    if (name.length < 2) {
        return "user.errors.name_too_short";
    }
    if (name.length > 45) {
        return "user.errors.name_too_long";
    }
    if (!isAllowedPersonName(name)) {
        return "user.errors.name_bad_character";
    }
    return null;
};

export const minutesAsDate = (minutes: number): Date => {
    const result = new Date(`1970-01-01T${minutesAsTimeString(minutes)}`);
    return result;
};

export const timeAsMinutes = (date: Date): number => {
    return date.getHours() * 60 + date.getMinutes();
};

export const minutesAsTimeString = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const minutesLeft = minutes % 60;
    return `${hours.toString().padStart(2, "0")}:${minutesLeft.toString().padStart(2, "0")}`;
};

export const hexToRgba = (color: string, alpha: number = 1) => {
    try {
        const colors = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(color);

        if (colors) {
            const r = parseInt(colors[1], 16);
            const g = parseInt(colors[2], 16);
            const b = parseInt(colors[3], 16);

            return `rgba(${r},${g},${b},${alpha})`;
        }
    } catch (_e) {
        /* empty */
    }

    console.warn(`hexToRgba's color is invalid (${color})`);
};

export const cantonsMapping = {
    AG: "Aargau",
    AI: "Appenzell Innerrhoden",
    AR: "Appenzell Ausserrhoden",
    BE: "Bern",
    BL: "Basel-Landschaft",
    BS: "Basel-Stadt",
    FR: "Fribourg",
    GE: "Genève",
    GL: "Glarus",
    GR: "Graubünden",
    JU: "Jura",
    LU: "Luzern",
    NE: "Neuchâtel",
    NW: "Nidwalden",
    OW: "Obwalden",
    SG: "St. Gallen",
    SH: "Schaffhausen",
    SO: "Solothurn",
    SZ: "Schwyz",
    TG: "Thurgau",
    TI: "Ticino",
    UR: "Uri",
    VD: "Vaud",
    VS: "Valais",
    ZG: "Zug",
    ZH: "Zürich",
};

export const avsMask = () => [
    /7/,
    /5/,
    /6/,
    ".",
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    ".",
    /\d/,
    /\d/,
    /\d/,
    /\d/,
    ".",
    /\d/,
    /\d/,
];

export const capitalizeEachFirstLetter = (text: string) =>
    text
        .toLowerCase()
        .split(" ")
        .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
        .join(" ");

export type NotificationData = {
    silent?: boolean;
    app_url?: string;
} & Record<string, unknown>;

export const getNotificationData = (notificationContent: NotificationContent): NotificationData => {
    const data = notificationContent.data;
    if (data) return data;
    const dataString = (notificationContent as { dataString?: string }).dataString;
    return JSON.parse(dataString || "{}") as NotificationData;
};

export type BalanceType = {
    text: string;
    date?: string;
};

// In Markdown, a line starting with `+`, `-` or `*` followed by a regular space opens a bullet
// list: `- 8h30m` renders as "• 8h30m", the sign is swallowed and a negative balance reads as a
// positive one. The non-breaking space defeats that parsing without changing the rendering, and
// the Unicode minus sign is not a list marker to begin with.
// Two dead ends, one already shipped and one tempting: HTML entities (`&plus;`/`&minus;`) and
// `escapeMarkdown`. Both assume a Markdown renderer; as plain text they show up literally
// (`&plus; 8h30m`, `\- 8h30m`). That was bug BAS-2839.
const POSITIVE_SIGN = "+\u00a0";
const NEGATIVE_SIGN = "\u2212\u00a0";

/**
 * Prefix a formatted value with its sign, in a form that is safe whichever way it is rendered
 * (plain text or Markdown).
 */
export const formatSignedBalance = (
    value: number,
    formatAbsoluteValue: (absoluteValue: number) => string,
) => {
    // Deliberately branching on comparisons rather than on `value === 0`, so that a
    // non-comparable value (NaN) falls back to "0" instead of yielding a signed "NaN".
    if (value > 0) {
        return `${POSITIVE_SIGN}${formatAbsoluteValue(value)}`;
    }
    if (value < 0) {
        return `${NEGATIVE_SIGN}${formatAbsoluteValue(Math.abs(value))}`;
    }
    return "0";
};

export const extractBoolean = (b: boolean | undefined | null) => {
    if (b === true) {
        return true;
    }
    if (b === false) {
        return false;
    }
    return undefined;
};

export type IjmSelection = {
    ijmGroupName?: string | null;
    ijmGroupId?: string | null;
    ijmPayFirstDays?: boolean | null;
    ijmPayRemainingPercents?: number | null;
};

export type MissionWithIjm = Mission & { ijmSelection?: IjmSelection };

export const getClientDeductionsRateWithoutLpp = (clientDeductions: SalaryDeductionType[]) =>
    clientDeductions.reduce(
        (result, deduction) =>
            deduction.title !== "lpp" ? result + parseFloat(deduction.rate) : result,
        0,
    );

export const getAmountWithClientDeductions = (
    amount: number,
    clientDeductions: SalaryDeductionType[],
) =>
    parseInt(
        (amount + (amount * getClientDeductionsRateWithoutLpp(clientDeductions)) / 100).toString(),
    );

export const toFixedIfNecessary = (value: number, maxDecimals: number = 2) => {
    return Math.round(value * Math.pow(10, maxDecimals)) / Math.pow(10, maxDecimals);
};

export function fromMaybe<T>(value: Maybe<T> | undefined): T | undefined {
    if (value === null || value === undefined) {
        return undefined;
    }
    return value;
}

export function onCurrencyChange(onChange: (v: number | string) => void) {
    return (v: string) => {
        if (v === "") {
            onChange("");
            return;
        }
        if (v.endsWith(".")) {
            onChange(v);
            return;
        }
        const n = Math.round(parseFloat(v) * 100) / 100;
        if (!isNaN(n)) {
            onChange(n);
        }
    };
}

export function onIntegerChange(onChange: (v: number | string) => void) {
    return (v: string) => {
        if (v === "") {
            onChange("");
            return;
        }
        const n = parseInt(v);
        if (!isNaN(n)) {
            onChange(n);
        }
    };
}

export function getMissionJobTitleDisplay(mission: CoreMissionFieldsFragment) {
    if (mission.currentContract?.jobTitle) {
        return mission.currentContract.jobTitle;
    }
    if (mission.activityType?.title) {
        return formatActivityTypeTitle(mission.activityType.title, mission.workerInfo?.gender);
    }
    return "";
}

export const sanitizeUrl = (inputUrl: string) => {
    try {
        let url = inputUrl.trim();

        // Force https://
        if (!/^https:\/\//i.test(url)) {
            url = url.replace(/^http:\/\//i, "https://");
            if (!/^https:\/\//i.test(url)) url = "https://" + url.replace(/^\/+/, "");
        }

        const parsed = new URL(url);
        parsed.search = ""; // delete all query params
        parsed.hash = ""; // delete hash

        return parsed.toString();
    } catch {
        return null;
    }
};

// Characters markdown-it (configured with html: true) treats as Markdown/HTML syntax.
// See app/components/MarkdownView.tsx for the parser configuration.
const MARKDOWN_ESCAPE_REGEX = /[\\`*_{}[\]()#+\-.!|>~<=&]/g;

/**
 * Backslash-escapes Markdown/HTML control characters so user-entered text renders literally
 * through MarkdownView instead of being parsed (e.g. `**name**` or `<img>` injected by a user).
 *
 * Use it only when a user value must be embedded in a developer-authored Markdown string, most
 * commonly an i18n interpolation: `i18n.t(key, { name: escapeMarkdown(user.name) })`.
 * When Markdown is not expected at all, prefer rendering plain text (no MarkdownView) instead.
 */
export const escapeMarkdown = (text: string | null | undefined): string =>
    (text ?? "").replace(MARKDOWN_ESCAPE_REGEX, "\\$&");

export const formatDate = (date: Date) => {
    return format(date, "dd.MM.yyyy");
};

/** Remove undefined values from an object recursively, but keep null values */
export function deepRemoveUndefined<T extends object>(obj: T): T {
    return Object.fromEntries(
        Object.entries(obj)
            .filter(([, value]) => value !== undefined)
            .map(([key, value]) => [
                key,
                value !== null && typeof value === "object" && !Array.isArray(value)
                    ? deepRemoveUndefined(value as object)
                    : value,
            ]),
    ) as T;
}

const USER_INTENTIONAL_FIREBASE_AUTH_ERRORS = [
    "auth/invalid-phone-number",
    "auth/invalid-verification-code",
];

export const captureFirebaseAuthException = (error: unknown) => {
    const code = (error as { code?: string }).code;
    if (code !== undefined && USER_INTENTIONAL_FIREBASE_AUTH_ERRORS.includes(code)) {
        return;
    }
    Sentry.captureException(error);
};

export type ContractLabelKind = "client" | "worker" | "client_employment";

/**
 * Un client lié à un legal employer hors régime LSE reçoit un contrat de travail
 * ("client_employment") et non un contrat de location ("client").
 */
export const getContractLabelKind = (
    profileType: ProfileType | undefined,
    clientUsesLse: boolean | undefined,
): ContractLabelKind => {
    if (profileType !== ProfileType.Client) {
        return "worker";
    }
    return clientUsesLse === false ? "client_employment" : "client";
};

/**
 * `lift` is the only payroll source whose timesheets are filled by the worker in the app.
 * Every other value — `manual_client`, `gekos`, or anything the backend adds later — means the
 * hours come from outside, so the app must show them read-only instead of assuming they are
 * editable. Unknown/missing values fall on the read-only side on purpose.
 */
export const isLiftTimesheetSource = (payrollSource?: PayrollSourceType | null) =>
    payrollSource === PayrollSourceType.Lift;

/**
 * Whether the mission's timesheets exist in the app at all. `gekos` timesheets are imported and
 * shown read-only, `manual_client` (and any unknown/missing source) has none to show.
 */
export const hasAppTimesheets = (payrollSource?: PayrollSourceType | null) =>
    payrollSource === PayrollSourceType.Lift || payrollSource === PayrollSourceType.Gekos;
